import { toast, Button, OtpInput } from "@risalabs_frontend_org/oasis-ui-kit";
import { QRCodeSVG } from "qrcode.react";
import { useState, useSyncExternalStore } from "react";
import {
  getMfaStateSnapshot,
  subscribeToMfaState,
  verifyTotpCode,
} from "../services/memberManagementService";
import RisaLogoWhite from "../svg/risa-logo-white";

const CODE_LENGTH = 6;

/**
 * Second-factor (TOTP) challenge screen.
 *
 * Rendered by ProtectRoute whenever a token exchange reports a pending MFA
 * challenge. Shows the enrollment QR for un-enrolled users, or just a code
 * input for enrolled users. On a valid code the challenge clears and the parent
 * proceeds to mint the full token.
 */
const MfaChallenge = () => {
  const mfa = useSyncExternalStore(
    subscribeToMfaState,
    getMfaStateSnapshot,
    getMfaStateSnapshot,
  );
  const [code, setCode] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState<boolean>(false);

  const isEnrollment = mfa.status === "enrollment";

  const isValidCode = (value: string) => /^\d{6}$/.test(value.trim());

  const handleVerify = async (codeToVerify?: string) => {
    const activeCode = codeToVerify || code;
    if (!isValidCode(activeCode)) {
      setError(`Enter the ${CODE_LENGTH}-digit code from your authenticator app`);
      return;
    }
    setIsVerifying(true);
    setError("");
    const result = await verifyTotpCode(activeCode.trim());
    setIsVerifying(false);
    if (!result.success) {
      const message = result.errorMessage ?? "Invalid code. Please try again.";
      setError(message);
      toast.error(message);
    }
    // On success the challenge state clears, unmounting this screen.
  };

  return (
    <div style={{ height: "100vh", width: "100vw", backgroundColor: "#000000", overflowY: "auto" }}>
      <div
        style={{
          display: "flex",
          minHeight: "100%",
          width: "100%",
          boxSizing: "border-box",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "2.5rem",
          padding: "2rem 1rem",
        }}
      >
        <div>
          <RisaLogoWhite width="120" height="42" />
        </div>
        <div
          style={{
            width: "25%",
            minWidth: "320px",
            maxWidth: "420px",
            borderRadius: "0.5rem",
            backgroundColor: "#ffffff",
            padding: "0.75rem",
          }}
        >
          <div className="mfa-challenge--container flex h-auto w-full flex-col items-center justify-center gap-3 p-4">
            <div className="mb-1 text-center text-large font-bold">
              {isEnrollment ? "Set up two-factor authentication" : "Two-factor authentication"}
            </div>

            {isEnrollment && (
              <div className="flex flex-col items-center gap-2">
                <div className="text-center text-small text-primaryGray-4">
                  Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password),
                  then enter the 6-digit code to finish enrollment.
                </div>
                {mfa.otpauthUri && (
                  <div style={{ padding: "0.75rem", backgroundColor: "#ffffff" }}>
                    <QRCodeSVG value={mfa.otpauthUri} size={176} />
                  </div>
                )}
              </div>
            )}

            {!isEnrollment && (
              <div className="text-center text-small text-primaryGray-4">
                Enter the 6-digit code from your authenticator app to continue.
              </div>
            )}

            <div className="flex w-full justify-center">
              <OtpInput
                value={code}
                onChange={setCode}
                length={CODE_LENGTH}
                label={"Authentication code"}
                required
                error={error}
                onComplete={handleVerify}
                disabled={isVerifying}
                autoFocus
              />
            </div>
            <div className="mt-2 w-full">
              <Button
                disabled={isVerifying || !isValidCode(code)}
                onClick={() => handleVerify()}
                buttonType={"primary"}
                size={"medium"}
                className="w-full"
              >
                {isVerifying ? "Verifying..." : "Verify"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaChallenge;
